import { Inject, Injectable, Optional } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { DbUtils, NAVIGATE_TO_ROOT, UrlUtils } from '@smooved/core';
import { iif, Observable, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { SURVEY_CONFIG } from '../constants/surveys.constants';
import { Interview } from '../interfaces/meta/interview';
import { SurveyConfig } from '../interfaces/survey-config.interface';
import { SurveysSandbox } from '../sandboxes/surveys.sandbox';
import { SurveyProvidersModule } from '../survey-providers.module';

@Injectable({
    providedIn: SurveyProvidersModule,
})
export abstract class SurveyStartActivator {
    constructor(
        @Optional() @Inject(SURVEY_CONFIG) private readonly config: SurveyConfig,
        private readonly router: Router,
        private readonly surveysSandbox: SurveysSandbox
    ) {}

    public canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
        return of(this.config?.canSelectInterviewee ?? true).pipe(take(1), switchMap(this.continueOrGoToInterview(route)));
    }

    private continueOrGoToInterview(route: ActivatedRouteSnapshot) {
        return (canActivate: boolean): Observable<boolean | UrlTree> => iif(() => canActivate, of(true), this.navigate(route));
    }

    private navigate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
        return this.surveysSandbox.interviewOnce$.pipe(
            switchMap((interview) => iif(() => !!interview, of(this.navigateToSurvey(route, interview)), of(this.navigateToDashboard())))
        );
    }

    private navigateToSurvey(route: ActivatedRouteSnapshot, { assessedTo, pages }: Interview): UrlTree {
        if (assessedTo && pages) {
            const { parentFragments: currentUrlFragments } = UrlUtils.getActivatedRoute(route);
            return this.router.createUrlTree([...currentUrlFragments, assessedTo, DbUtils.getStringId(pages[0])]);
        }
        return this.navigateToDashboard();
    }

    private navigateToDashboard(): UrlTree {
        return this.router.createUrlTree(NAVIGATE_TO_ROOT);
    }
}
